/**
 * 店舗タグ関連のQuery Hooks（FKV3-13002）
 *
 * 更新系はすべて成功後に invalidate して再取得する。楽観更新はしない
 * （タグ名ごとの付与店舗数がズレたまま残ると、CSが何件付いているか判断できなくなるため）。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  bulkUpdateSellerTags,
  createSellerTagType,
  createSellerTags,
  deleteSellerTagType,
  deleteSellerTag,
  getSellerTagAssignments,
  getSellerTagTypes,
  replaceSellerTagAssignments,
  resolveTargetWarehouses,
  updateSellerTagType,
  updateSellerTag,
} from '@/lib/api/seller-tag';
import type {
  BulkSellerTagRequest,
  CreateSellerTagTypeRequest,
  CreateSellerTagsRequest,
  ReplaceSellerTagAssignmentsRequest,
  ResolveWarehousesRequest,
  UpdateSellerTagTypeRequest,
  UpdateSellerTagRequest,
} from '@/lib/types/feature/seller-tag';
import { sellerTagKeys } from '../keys';

/** タグ種別一覧（配下のタグ名と付与店舗数込み） */
export function useSellerTagTypes(accountCode: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: sellerTagKeys.types(accountCode),
    queryFn: async () => getSellerTagTypes(accountCode, getAuthHeader()),
    enabled: !authHeaderLoading && isAuthorized && !!accountCode,
  });
}

/** 店舗へのタグ付与 */
export function useSellerTagAssignments(accountCode: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: sellerTagKeys.assignments(accountCode),
    queryFn: async () => getSellerTagAssignments(accountCode, getAuthHeader()),
    enabled: !authHeaderLoading && isAuthorized && !!accountCode,
  });
}

/** タグ種別・タグ名・付与のキャッシュをまとめて捨てる */
function useInvalidateSellerTags(accountCode: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: sellerTagKeys.all() });
    void queryClient.invalidateQueries({ queryKey: sellerTagKeys.types(accountCode) });
    void queryClient.invalidateQueries({ queryKey: sellerTagKeys.assignments(accountCode) });
  };
}

export function useCreateSellerTagType(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async (request: CreateSellerTagTypeRequest) =>
      createSellerTagType(accountCode, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

export function useUpdateSellerTagType(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({
      tagTypeId,
      request,
    }: {
      tagTypeId: string;
      request: UpdateSellerTagTypeRequest;
    }) => updateSellerTagType(accountCode, tagTypeId, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

export function useDeleteSellerTagType(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({ tagTypeId, cascade }: { tagTypeId: string; cascade: boolean }) =>
      deleteSellerTagType(accountCode, tagTypeId, cascade, getAuthHeader()),
    onSuccess: invalidate,
  });
}

export function useCreateSellerTags(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({
      tagTypeId,
      request,
    }: {
      tagTypeId: string;
      request: CreateSellerTagsRequest;
    }) => createSellerTags(accountCode, tagTypeId, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

export function useUpdateSellerTag(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({ tagId, request }: { tagId: string; request: UpdateSellerTagRequest }) =>
      updateSellerTag(accountCode, tagId, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

export function useDeleteSellerTag(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({ tagId, cascade }: { tagId: string; cascade: boolean }) =>
      deleteSellerTag(accountCode, tagId, cascade, getAuthHeader()),
    onSuccess: invalidate,
  });
}

/** 付与・剥奪の差分適用 */
export function useBulkUpdateSellerTags(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async (request: BulkSellerTagRequest) =>
      bulkUpdateSellerTags(accountCode, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

/** タグ種別単位の洗い替え（「CSVに無い店舗のタグを外す」を選んだときだけ使う） */
export function useReplaceSellerTagAssignments(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();
  const invalidate = useInvalidateSellerTags(accountCode);

  return useMutation({
    mutationFn: async ({
      tagTypeId,
      request,
    }: {
      tagTypeId: string;
      request: ReplaceSellerTagAssignmentsRequest;
    }) => replaceSellerTagAssignments(accountCode, tagTypeId, request, getAuthHeader()),
    onSuccess: invalidate,
  });
}

/**
 * 店舗タグで指定した対象店舗を倉庫IDへ解決する（FKV3-13004 ①）。
 *
 * 呼ぶたびに店舗マスタの最新で解決したいのでキャッシュはしない（mutation で扱う）。
 */
export function useResolveTargetWarehouses(accountCode: string) {
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: ResolveWarehousesRequest) =>
      resolveTargetWarehouses(accountCode, request, getAuthHeader()),
  });
}
